import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Zap } from 'lucide-react';
import { CreditTransactionsList } from '@/components/credit-transactions-list';
import { useNavigate } from 'react-router-dom';

interface CreditStatsDashboardProps {
  availableCredits: number | null;
  totalCredits: number | null;
  transactions: any[];
  isLoading?: boolean;
}

export function CreditStatsDashboard({ 
  availableCredits, 
  totalCredits, 
  transactions, 
  isLoading = false 
}: CreditStatsDashboardProps) {
  const navigate = useNavigate();
  
  // Handle null values safely
  const currentAvailable = availableCredits ?? 0;
  const currentTotal = totalCredits ?? 0;
  
  // Calculate progress value safely
  const progressValue = currentTotal > 0 ? (currentAvailable / currentTotal) * 100 : 0;

  return (
    <Card className="overflow-hidden border shadow-md">
      <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5 pb-8">
        <CardTitle className="text-xl md:text-2xl">크레딧 현황</CardTitle>
      </CardHeader>
      <div className="-mt-6">
        <div className="mx-6 rounded-xl bg-card p-6 shadow-md">
          {/* 크레딧 현황 박스 */}
          <div className="grid gap-6 sm:grid-cols-3">
            <div className="rounded-xl border bg-card/50 p-5 text-center backdrop-blur-sm transition-all hover:bg-card">
              <div className="text-sm text-muted-foreground mb-1">사용 가능</div>
              <div className="text-3xl font-bold">{isLoading ? '-' : currentAvailable}</div>
            </div>

         

            <div className="rounded-xl border bg-card/50 p-5 text-center backdrop-blur-sm transition-all hover:bg-card">
              <div className="text-sm text-muted-foreground mb-1">총 수령</div>
              <div className="text-3xl font-bold">{isLoading ? '-' : currentTotal}</div>
            </div>
          </div>

          {/* 소비 현황 프로그레스 바 */}
          <div className="mt-6">
            <div className="mb-1 mt-2 flex justify-between text-xs text-muted-foreground">
              <span>{isLoading ? '로딩 중...' : `${currentAvailable} / ${currentTotal} 크레딧`}</span>
            </div>
            <Progress 
              value={isLoading ? 0 : progressValue} 
              aria-label="크레딧 소비 현황" 
              className="h-2.5" 
            />
          </div>

          {/* 크레딧 부족 알림 */}
          {!isLoading && availableCredits !== null && availableCredits <= 20 && (
            <Alert className="mt-6 rounded-lg bg-amber-50/60 dark:bg-amber-950/30 border-amber-300/60 dark:border-amber-800/60">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertTitle className="text-amber-800 dark:text-amber-300">크레딧 부족 알림</AlertTitle>
              <AlertDescription className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between text-amber-700 dark:text-amber-400">
                <span>
                  {availableCredits === 0 ? '사용 가능한 크레딧이 없습니다.' : `남은 크레딧이 ${availableCredits}개 입니다.`} 충전이 필요합니다.
                </span>
                <Button
                  size="sm"
                  onClick={() => navigate('/pricing')}
                  className="mt-2 sm:mt-0 sm:ml-auto shrink-0 gradient-button text-white"
                >
                  크레딧 충전하기
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
      
      <CardContent className="space-y-5 p-6">
        {/* 크레딧 사용 통계 */}
       
        
        {/* 최근 거래 내역 */}
        <div className="space-y-3">
          <h3 className="font-semibold">최근 거래 내역</h3>
          <CreditTransactionsList transactions={transactions} isLoading={isLoading} />
        </div>

        {/* 크레딧 구매 버튼 */}
        <div className="flex justify-center pt-4">
          <Button 
            onClick={() => navigate('/pricing')}
            className="gradient-button text-lg font-medium py-6"
            size="lg"
          >
            <Zap className="mr-2 h-5 w-5" />
            크레딧 구매 페이지로 이동
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}